import dayjs from 'dayjs';
import LessonRescheduleFormFilters from './LessonRescheduleFormFilters';
import { Box, Button, Checkbox, Text, VStack } from '@chakra-ui/react';
import { isLocationRoomAvailable, isTeacherAvailable } from '@opanuj/shared/slots';
import { formatRescheduledLesson } from '@opanuj/shared/utils';
import { addRangeToPeriods } from '@opanuj/shared/utils';
import { generateLessonsTimestamps } from '@opanuj/shared/lessons';
import { Lesson, LessonStatus, Location, Slot, SlotExpanded } from '@opanuj/shared/types';
import { useMemo, useState } from 'react';
import { useSettings } from '../../../../hooks/useSettings';
import { useSlotsList } from '../../../../hooks/useSlots';
import { useUser } from '../../../../hooks/useUser';
// import { useLessonsList } from "../../../../hooks/useLessons"

export type LessonRescheduleFormProps = {
	lesson: Lesson
	onSubmit: (options: Lesson[]) => void
}

const endDate = dayjs().add(1, 'month').endOf('month');

const LessonRescheduleForm = ({ onSubmit, lesson }: LessonRescheduleFormProps) => {
	const [selectedOptions, setSelectedOptions] = useState<Lesson[]>([])
	const [teachersFilter, setTeacherFilter] = useState<string[]>([])
	const [daysFilter, setDaysFilter] = useState<string[]>([])
	const [locationsFilter, setLocationsFilter] = useState<Location[]>([])

	const [user, userLoading, userError] = useUser()
	const [settings, settingsLoading, settingsError] = useSettings();
	// const [lessons, lessonsLoading, lessonsError] = useLessonsList();
	const [slots, slotsLoading, slotsError] = useSlotsList();

	const relevantStudents = useMemo(() => lesson.students.filter((s) => s.uid === user?.uid), [lesson.students, user?.uid]);

	// const relevantLessons = useMemo(() => lessons?.filter((l: Lesson) =>
	// 	l.code === lesson.code
	// 	&& l.duration === lesson.duration
	// 	&& relevantStudents.find((rs) => l.students.find((ls) => ls.uid === rs.uid && ls.status !== LessonStatus.Cancelled))
	// ), [lessons, lesson.code, lesson.duration, relevantStudents])

	const relevantSlots = useMemo(() => {
		if (!slots) return [];

		const slotsData = addRangeToPeriods(slots) as unknown as SlotExpanded[];
		return slots.filter((s: Slot) => {
			if (s.hidden || s.code !== lesson.code || s.duration !== lesson.duration) return false;

			const slotData = slotsData.find((candidate) => candidate.id === s.id);
			return Boolean(
				slotData &&
				isLocationRoomAvailable(slotData, [], slotsData) &&
				isTeacherAvailable(s.defaultTeacher, slotData, [], slotsData)
			);
		});
	}, [slots, lesson.code, lesson.duration])

	const allOptions = useMemo(
		() =>
			relevantSlots?.flatMap((slot: Slot) => {
				if (
					teachersFilter.length > 0 &&
					!teachersFilter.includes(slot.defaultTeacher)
				)
					return [];
				if (daysFilter.length > 0 && !daysFilter.includes(slot.day))
					return [];
				if (
					locationsFilter.length > 0 &&
					!locationsFilter.includes(slot.location)
				)
					return [];
				if (slot.started && slot.lessons) {
					return slot.lessons.filter((l) => l.status === LessonStatus.Cancelled).map((l) => ({
						...l,
						id: `${slot.id}_${l.timestamp}`, //stable ids, probably not final
						timestamp: l.timestamp,
						status: LessonStatus.Extra,
						students: relevantStudents.map((s) => ({ ...s, status: LessonStatus.Extra })),
					}));
				} else {
					const timestamps = generateLessonsTimestamps({
						start: dayjs().add(1, 'day'),
						end: endDate,
						daysOff: (settings?.daysOff ?? []).map(dayjs),
						day: slot.day,
						time: slot.time,
					});
					return timestamps.map(
						(timestamp) =>
							({
								id: `${slot.id}_${timestamp.unix()}`, //stable ids, probably not final
								status: LessonStatus.Extra,
								sourceSlot: slot.id,
								code: slot.code,
								timestamp: timestamp.unix(),
								duration: slot.duration,
								location: slot.location,
								room: slot.room,
								teacher: slot.defaultTeacher,
								students: relevantStudents.map((s) => ({ ...s, status: LessonStatus.Extra })),
							} as Lesson)
					);

				}
			}),
		[relevantSlots, teachersFilter, daysFilter, locationsFilter, settings?.daysOff, relevantStudents]
	);
	allOptions?.sort((l1, l2) => l1.timestamp - l2.timestamp)

	let teachersOptions = allOptions?.reduce((acc, option) => {
		return acc.includes(option.teacher)
			? acc
			: acc.concat(option.teacher);
	}, [] as string[]);

	
	if (userLoading || userError || !user) return null;
	if (slotsLoading || slotsError || !slots) return null;
	// if (lessonsLoading || lessonsError || !lessons) return null;
	if (settingsLoading || settingsError || !settings) return null;

	return (
        <Box>
            <Text textStyle="italic">
				Wybierz z puli poniżej propozycje terminów, które Ci odpowiadają:
			</Text>
            <LessonRescheduleFormFilters
				teachersOptions={teachersOptions!}
				teachersValue={teachersFilter}
				onTeachersChange={setTeacherFilter}
				daysValue={daysFilter}
				onDaysChange={setDaysFilter}
				locationsValue={locationsFilter}
				onLocationsChange={setLocationsFilter}
			/>
            <Button
				m={2}
				onClick={() => onSubmit(selectedOptions)}
				colorPalette="blue"
				disabled={!selectedOptions.length}
			>
				{selectedOptions.length ? `Wyślij (${selectedOptions.length})` : 'Wyślij'}
			</Button>
            <VStack alignItems="start" maxH="500px" overflow="scroll">
				{allOptions!.map((option) => {
					const isSelected = !!selectedOptions.find(
						(x) => x.id === option.id
					);
					return (
                        <Checkbox.Root
							checked={isSelected}
							// variant="plain"
							colorPalette="orange"
							key={option.id}
							onCheckedChange={() => {
								isSelected
									? setSelectedOptions(
											selectedOptions.filter(
												(x) => x.id !== option.id
											)
									  )
									: setSelectedOptions(
											selectedOptions.concat(option)
									  );
							}}
						><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                                {formatRescheduledLesson(option)}
                            </Checkbox.Label></Checkbox.Root>
                    );
				})}
			</VStack>
        </Box>
    );
}

export default LessonRescheduleForm